/**
 * UI Controller - Manages all UI interactions and updates
 */

interface UICallbacks {
  onPlayToggle: () => void;
  onTempoChange: (tempo: number) => void;
  onTrackMute: (trackIndex: number) => void;
}

interface DebugInfo {
  currentSample: number;
  contextTime: number;
  loopStatus: string;
  playbackMode: string;
  audioState: string;
}

interface ConstantsInfo {
  beatsPerTrack: number;
  syncLengthSamples: number;
}

interface LevelMeterData {
  peak: number;
  trackIndex: number;
}

export class UIController {
  private callbacks: UICallbacks;
  private activePointers: Map<number, { trackIndex: number; startY: number }> = new Map();
  private trackNames: string[] = [];

  constructor(callbacks: UICallbacks) {
    this.callbacks = callbacks;
  }

  /**
   * Initialize UI event listeners
   */
  initialize(): void {
    this.initializePlayButton();
    this.initializeTempoControl();
  }

  /**
   * Initialize play/stop button
   */
  private initializePlayButton(): void {
    const playToggle = document.getElementById('play-toggle');

    if (playToggle) {
      playToggle.style.touchAction = 'none';

      playToggle.addEventListener('pointerdown', () => {
        playToggle.classList.add('touching');
      });

      playToggle.addEventListener('pointerup', async () => {
        playToggle.classList.remove('touching');
        this.callbacks.onPlayToggle();
      });

      playToggle.addEventListener('pointercancel', () => {
        playToggle.classList.remove('touching');
      });
    }
  }

  /**
   * Initialize tempo slider
   */
  private initializeTempoControl(): void {
    const tempoSlider = document.getElementById('tempo-slider') as HTMLInputElement;
    const tempoValue = document.getElementById('tempo-value');

    if (tempoSlider) {
      tempoSlider.style.touchAction = 'pan-y';

      tempoSlider.addEventListener('input', () => {
        const tempo = parseInt(tempoSlider.value);
        if (tempoValue) {
          tempoValue.textContent = tempo.toString();
        }
        this.callbacks.onTempoChange(tempo);
      });
    }
  }

  /**
   * Generate track UI elements
   */
  generateTrackUI(trackNames: string[]): void {
    this.trackNames = trackNames;
    const container = document.getElementById('tracks-container');
    if (!container) {
      return;
    }

    // Clear existing tracks
    container.innerHTML = '';

    // Generate HTML for each track
    trackNames.forEach((name, index) => {
      const trackDiv = document.createElement('div');
      trackDiv.className = 'track';
      trackDiv.setAttribute('data-track', index.toString());

      trackDiv.innerHTML = `
        <h3 class="track-title muted loading" data-track="${index}">${name}</h3>
      `;

      container.appendChild(trackDiv);
    });

    // Set up track event listeners
    this.initializeTrackEventListeners();
  }

  /**
   * Initialize track event listeners
   */
  private initializeTrackEventListeners(): void {
    document.querySelectorAll('.track-title').forEach(title => {
      const titleElement = title as HTMLElement;
      titleElement.style.touchAction = 'none';

      title.addEventListener('pointerdown', e => {
        const event = e as PointerEvent;

        // Only allow interaction if tracks are loaded
        if (!title.classList.contains('loaded')) {
          console.log('⏳ Tracks still loading, please wait...');
          return;
        }

        const trackIndex = parseInt(title.getAttribute('data-track') || '0');

        // Store pointer info for multi-touch tracking
        this.activePointers.set(event.pointerId, {
          trackIndex,
          startY: event.clientY
        });

        // Add visual feedback
        title.classList.add('touching');
      });

      title.addEventListener('pointerup', e => {
        const event = e as PointerEvent;

        // Check if this pointer was tracked
        const pointerInfo = this.activePointers.get(event.pointerId);
        if (pointerInfo) {
          // Remove pointer from tracking
          this.activePointers.delete(event.pointerId);

          // Remove visual feedback
          title.classList.remove('touching');

          // Toggle mute
          this.callbacks.onTrackMute(pointerInfo.trackIndex);
        }
      });

      title.addEventListener('pointercancel', e => {
        const event = e as PointerEvent;
        this.activePointers.delete(event.pointerId);
        title.classList.remove('touching');
      });

      title.addEventListener('pointerleave', e => {
        const event = e as PointerEvent;
        if (this.activePointers.has(event.pointerId)) {
          this.activePointers.delete(event.pointerId);
          title.classList.remove('touching');
        }
      });
    });
  }

  /**
   * Update play button appearance
   */
  updatePlayButton(isPlaying: boolean): void {
    const playToggle = document.getElementById('play-toggle');
    if (playToggle) {
      if (isPlaying) {
        playToggle.textContent = '■';
        playToggle.classList.add('playing');
      } else {
        playToggle.textContent = '▶';
        playToggle.classList.remove('playing');
      }
    }
  }

  /**
   * Show play button
   */
  showPlayButton(): void {
    const playToggle = document.getElementById('play-toggle');
    if (playToggle) {
      (playToggle as HTMLElement).style.display = '';
    }
  }

  /**
   * Hide play button
   */
  hidePlayButton(): void {
    const playToggle = document.getElementById('play-toggle');
    if (playToggle) {
      (playToggle as HTMLElement).style.display = 'none';
    }
  }

  /**
   * Update track status display
   */
  updateTrackStatus(
    trackIndex: number,
    status: 'loading' | 'ready' | 'playing' | 'paused' | 'stopped'
  ): void {
    const trackTitle = document.querySelector(
      `.track-title[data-track="${trackIndex}"]`
    ) as HTMLElement;

    if (trackTitle && this.trackNames[trackIndex]) {
      const trackName = this.trackNames[trackIndex];

      if (status === 'loading') {
        trackTitle.textContent = `${trackName} (Loading...)`;
        trackTitle.classList.add('loading');
      } else if (status === 'ready') {
        trackTitle.textContent = trackName;
        trackTitle.classList.remove('loading');
        trackTitle.classList.add('ready');
        // Remove ready class after animation
        setTimeout(() => trackTitle.classList.remove('ready'), 1000);
      } else {
        trackTitle.textContent = trackName;
      }
    }
  }

  /**
   * Mark all tracks as loaded
   */
  markAllTracksLoaded(): void {
    document.querySelectorAll('.track-title').forEach(title => {
      title.classList.add('loaded');
    });
  }

  /**
   * Update track mute visual state
   */
  updateTrackMuteState(trackIndex: number, isMuted: boolean): void {
    const trackTitle = document.querySelector(
      `.track-title[data-track="${trackIndex}"]`
    ) as HTMLElement;

    if (trackTitle) {
      if (isMuted) {
        trackTitle.classList.add('muted');
      } else {
        trackTitle.classList.remove('muted');
      }
    }
  }

  /**
   * Reset all tracks to muted state
   */
  resetAllTracksMuted(): void {
    document.querySelectorAll('.track-title').forEach(title => {
      title.classList.add('muted');
    });
  }

  /**
   * Update beat display
   */
  updateBeatDisplay(beat: number): void {
    const beatElement = document.getElementById('current-beat');
    if (beatElement) {
      beatElement.textContent = beat.toString();
    }
  }

  /**
   * Update debug counters
   */
  updateDebugCounters(info: DebugInfo): void {
    const sampleElement = document.getElementById('current-sample');
    if (sampleElement) {
      sampleElement.textContent = info.currentSample.toString();
    }

    const contextTimeElement = document.getElementById('context-time');
    if (contextTimeElement) {
      contextTimeElement.textContent = info.contextTime.toFixed(2);
    }

    const loopStatusElement = document.getElementById('loop-status');
    if (loopStatusElement) {
      loopStatusElement.textContent = info.loopStatus;
    }

    const playbackModeElement = document.getElementById('playback-mode');
    if (playbackModeElement) {
      playbackModeElement.textContent = info.playbackMode;
    }

    const audioStateElement = document.getElementById('audio-state');
    if (audioStateElement) {
      audioStateElement.textContent = info.audioState;
    }
  }

  /**
   * Update constants display
   */
  updateConstantsDisplay(constants: ConstantsInfo): void {
    const totalBeatsElement = document.getElementById('total-beats');
    if (totalBeatsElement) {
      totalBeatsElement.textContent = constants.beatsPerTrack.toString();
    }

    const syncLengthSamplesElement = document.getElementById('sync-length-samples');
    if (syncLengthSamplesElement) {
      syncLengthSamplesElement.textContent = constants.syncLengthSamples.toString();
    }
  }

  /**
   * Update level meters
   */
  updateLevelMeters(meterData: LevelMeterData[]): void {
    meterData.forEach(({ peak, trackIndex }) => {
      const trackTitle = document.querySelector(
        `.track-title[data-track="${trackIndex}"]`
      ) as HTMLElement;

      if (trackTitle) {
        const peakLevel = peak;

        // Update the ::before pseudo-element height via CSS variable
        trackTitle.style.setProperty('--level-height', `${peakLevel}%`);

        // Remove all level classes first
        trackTitle.classList.remove('has-signal', 'level-yellow', 'level-orange', 'level-red');

        // Add appropriate class based on peak level
        if (peakLevel > 85) {
          trackTitle.classList.add('level-red');
        } else if (peakLevel > 70) {
          trackTitle.classList.add('level-orange');
        } else if (peakLevel > 50) {
          trackTitle.classList.add('level-yellow');
        } else if (peakLevel > 5) {
          trackTitle.classList.add('has-signal');
        }
      }
    });
  }

  /**
   * Reset level meters
   */
  resetLevelMeters(): void {
    document.querySelectorAll('.track-title').forEach(title => {
      (title as HTMLElement).style.setProperty('--level-height', '0%');
      title.classList.remove('has-signal', 'level-yellow', 'level-orange', 'level-red');
    });
  }
}
